import { useState, useEffect, useRef, useCallback } from 'react';
import { Modal } from './Modal';
import { Input } from './Input';
import { Button } from './Button';
import { ConfirmDialog } from './ConfirmDialog';
import type { Concert, Band, Venue, ChecklistItem, Address } from '../../types';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { venuesAPI } from '../../api/endpoints';
import { ExternalLink } from 'lucide-react';
import { isNegotiatedComplete, isChecklistComplete } from './concertUtils';

interface ConcertModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ConcertFormData) => void;
  onDelete?: () => void;
  concert?: Concert | null;
  mode?: 'create' | 'edit';
  bands: Band[];
  venues?: Venue[];
  defaultVenueId?: string;
  defaultVenueName?: string;
  onOpenVenueContact?: (venueId: string) => void;
}

export interface ConcertFormData {
  date: string;
  band_id: string;
  venue_id: string;
  status: 'done' | 'canceled' | 'in_discussion';
  // Negotiated
  total_amount: string;
  benefits: string;
  times: string;
  duration: string;
  backline: string;
  technique: string;
  other_info: string;
  // Progress
  admin_contact: string;
  checklist: ChecklistItem[];
}

const initialFormData: ConcertFormData = {
  date: '',
  band_id: '',
  venue_id: '',
  status: 'in_discussion',
  total_amount: '',
  benefits: '',
  times: '',
  duration: '',
  backline: '',
  technique: '',
  other_info: '',
  admin_contact: '',
  checklist: [],
};

const STATUS_OPTIONS = [
  { value: 'in_discussion', label: 'In Discussion' },
  { value: 'done', label: 'Done' },
  { value: 'canceled', label: 'Canceled' },
] as const;

const MAX_VENUE_RESULTS = 8;
const DEBOUNCE_MS = 250;

function VenueSearchField({ venues, value, onChange, onAddressChange, initialVenueName, onOpenVenueContact }: { venues?: Venue[]; value: string; onChange: (id: string) => void; onAddressChange?: (address: Address | undefined) => void; initialVenueName?: string; onOpenVenueContact?: (venueId: string) => void }) {
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [results, setResults] = useState<Venue[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedName, setSelectedName] = useState(initialVenueName || '');
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const abortRef = useRef<AbortController>(undefined);

  // Update selectedName and address when venue changes
  useEffect(() => {
    if (value) {
      if (initialVenueName) {
        setSelectedName(initialVenueName);
      }
      // Always fetch venue to get the address (and name if not provided)
      venuesAPI.getById(value).then((res) => {
        if (!initialVenueName) setSelectedName(res.data.name);
        onAddressChange?.(res.data.address);
      }).catch(() => {});
    } else {
      setSelectedName('');
      onAddressChange?.(undefined);
    }
  }, [initialVenueName, value, onAddressChange]);

  const displayValue = isFocused ? query : selectedName;

  const doSearch = useCallback(async (searchQuery: string) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // If venues are provided client-side (small dataset), filter locally
    if (venues) {
      if (!searchQuery.trim()) {
        setResults(venues.slice(0, MAX_VENUE_RESULTS));
      } else {
        const lower = searchQuery.toLowerCase();
        const filtered: Venue[] = [];
        for (const v of venues) {
          if (v.name.toLowerCase().includes(lower)) {
            filtered.push(v);
            if (filtered.length >= MAX_VENUE_RESULTS) break;
          }
        }
        setResults(filtered);
      }
      setIsSearching(false);
      return;
    }

    // Server-side search
    try {
      setIsSearching(true);
      const response = await venuesAPI.list(
        { q: searchQuery || undefined, page_size: MAX_VENUE_RESULTS },
        controller.signal,
      );
      if (!controller.signal.aborted) {
        setResults(response.data.items);
      }
    } catch {
      // Ignore aborted requests
    } finally {
      if (!controller.signal.aborted) setIsSearching(false);
    }
  }, [venues]);

  // Debounced search on query change
  useEffect(() => {
    if (!isFocused) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(query), DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [query, isFocused, doSearch]);

  // Load initial results when focused
  const handleFocus = () => {
    setIsFocused(true);
    setQuery('');
    doSearch('');
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsFocused(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="flex flex-col gap-1 relative" ref={containerRef}>
      <div className="flex items-center gap-1.5">
        <label className="text-sm font-medium text-dark-text-secondary">Venue *</label>
        {value && onOpenVenueContact && (
          <button
            type="button"
            onClick={() => onOpenVenueContact(value)}
            className="text-dark-text-muted hover:text-primary-400 transition-colors"
            title="Open venue contacts"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <input
        className="input"
        type="text"
        placeholder="Search venues..."
        value={displayValue}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!e.target.value.trim()) onChange('');
        }}
        onFocus={handleFocus}
      />
      {isFocused && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-48 overflow-y-auto bg-dark-bg-secondary border border-dark-border rounded-lg shadow-lg">
          {results.map((v) => (
            <button
              key={v._id}
              type="button"
              className={`w-full text-left px-3 py-2 text-sm hover:bg-dark-bg-tertiary transition-colors ${
                v._id === value ? 'text-primary-400 bg-dark-bg-tertiary' : 'text-dark-text-primary'
              }`}
              onClick={() => {
                onChange(v._id);
                setSelectedName(v.name);
                onAddressChange?.(v.address);
                setIsFocused(false);
                setQuery('');
              }}
            >
              {v.name}
              {v.address?.city && (
                <span className="text-dark-text-muted ml-2 text-xs">{v.address.city}</span>
              )}
            </button>
          ))}
        </div>
      )}
      {isFocused && !isSearching && query.trim() && results.length === 0 && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-dark-bg-secondary border border-dark-border rounded-lg shadow-lg px-3 py-2 text-sm text-dark-text-muted">
          No venues found
        </div>
      )}
      {isFocused && isSearching && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-dark-bg-secondary border border-dark-border rounded-lg shadow-lg px-3 py-2 text-sm text-dark-text-muted">
          Searching...
        </div>
      )}
    </div>
  );
}

function TextAreaField({ label, placeholder, value, onChange }: { label: string; placeholder?: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium text-dark-text-secondary">{label}</label>
      <textarea
        rows={1}
        className="input resize-y text-sm"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function SectionCheck({ complete, color }: { complete: boolean; color?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={`w-4 h-4 inline-block ml-1.5 ${complete ? (color || 'text-green-400') : 'text-dark-text-muted/40'}`}
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  );
}

export function ConcertModal({ isOpen, onClose, onSubmit, onDelete, concert, mode = 'create', bands, venues, defaultVenueId, defaultVenueName, onOpenVenueContact }: ConcertModalProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState<ConcertFormData>(initialFormData);
  const [error, setError] = useState('');
  const [venueAddress, setVenueAddress] = useState<Address | undefined>(undefined);
  const [checklistDeleteIdx, setChecklistDeleteIdx] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (concert && mode === 'edit') {
        setFormData({
          date: concert.date ? format(new Date(concert.date), "yyyy-MM-dd") : '',
          band_id: concert.band_id || '',
          venue_id: concert.venue_id || '',
          status: concert.status || 'in_discussion',
          total_amount: concert.total_amount || '',
          benefits: concert.benefits || '',
          times: concert.times || '',
          duration: concert.duration || '',
          backline: concert.backline || '',
          technique: concert.technique || '',
          other_info: concert.other_info || '',
          admin_contact: concert.admin_contact || '',
          checklist: concert.checklist || [],
        });
      } else {
        const defaultChecklist: ChecklistItem[] = (user?.settings?.default_concert_checklist ?? []).map(
          (label) => ({ label, done: false })
        );
        setFormData({
          ...initialFormData,
          band_id: bands.length === 1 ? bands[0]._id : '',
          venue_id: defaultVenueId || '',
          checklist: defaultChecklist,
        });
      }
      setError('');
    }
  }, [isOpen, concert, mode, bands, defaultVenueId, user?.settings?.default_concert_checklist]);

  const handleSubmit = () => {
    setError('');
    if (!formData.band_id) { setError('Band is required'); return; }
    if (!formData.venue_id) { setError('Venue is required'); return; }
    onSubmit(formData);
    resetAndClose();
  };

  const handleDelete = () => {
    if (!onDelete) return;
    onDelete();
    resetAndClose();
  };

  const resetAndClose = () => {
    setFormData(initialFormData);
    setVenueAddress(undefined);
    setError('');
    onClose();
  };

  const updateField = (field: keyof ConcertFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={resetAndClose}
      title={mode === 'edit' ? 'Edit Concert' : 'New Concert'}
      size="6xl"
      footer={
        <div className="flex items-center justify-between w-full">
          <div>
            {mode === 'edit' && onDelete && (
              <Button variant="ghost" onClick={handleDelete}>
                <span className="text-red-400">Delete</span>
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={resetAndClose}>Cancel</Button>
            <Button variant="primary" onClick={handleSubmit}>
              {mode === 'edit' ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="p-3 bg-red-900/20 border border-red-700/50 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          {/* Column 1: Essentials */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-dark-text-primary border-b border-dark-border pb-1.5">
              Essentials
              <SectionCheck complete={formData.status === 'done' || formData.status === 'canceled'} color={formData.status === 'canceled' ? 'text-red-400' : undefined} />
            </h3>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-dark-text-secondary">Band *</label>
              <select
                className="input"
                value={formData.band_id}
                onChange={(e) => updateField('band_id', e.target.value)}
              >
                <option value="">Select band...</option>
                {bands.map((b) => (
                  <option key={b._id} value={b._id}>{b.name}</option>
                ))}
              </select>
            </div>

            <VenueSearchField
              venues={venues}
              value={formData.venue_id}
              onChange={(venueId) => updateField('venue_id', venueId)}
              onAddressChange={setVenueAddress}
              initialVenueName={defaultVenueName}
              onOpenVenueContact={onOpenVenueContact}
            />

            {venueAddress && (
              <div className="flex flex-col gap-0.5">
                <label className="text-sm font-medium text-dark-text-secondary">Address</label>
                <div className="text-xs text-dark-text-muted leading-relaxed">
                  {venueAddress.street && <div>{venueAddress.street}</div>}
                  {(venueAddress.zip_code || venueAddress.city) && (
                    <div>{[venueAddress.zip_code, venueAddress.city].filter(Boolean).join(' ')}</div>
                  )}
                  {venueAddress.administrative_region_1 && <div>{venueAddress.administrative_region_1}</div>}
                  {venueAddress.country && <div>{venueAddress.country}</div>}
                  {!venueAddress.city && !venueAddress.street && venueAddress.unparsed_address && (
                    <div>{venueAddress.unparsed_address}</div>
                  )}
                </div>
              </div>
            )}

            <Input
              label="Date"
              type="date"
              value={formData.date}
              onChange={(e) => updateField('date', e.target.value)}
            />

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-dark-text-secondary">Status</label>
              <select
                className="input"
                value={formData.status}
                onChange={(e) => updateField('status', e.target.value)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Column 2: Negotiated */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-dark-text-primary border-b border-dark-border pb-1.5">
              Negotiated
              <SectionCheck complete={isNegotiatedComplete(formData)} />
            </h3>

            <TextAreaField
              label="Total Amount"
              placeholder="Price (tax included)..."
              value={formData.total_amount}
              onChange={(v) => updateField('total_amount', v)}
            />
            <TextAreaField
              label="Benefits"
              placeholder="Travel, housing, catering..."
              value={formData.benefits}
              onChange={(v) => updateField('benefits', v)}
            />
            <TextAreaField
              label="Times"
              placeholder="Arrival, get in, soundcheck..."
              value={formData.times}
              onChange={(v) => updateField('times', v)}
            />
            <TextAreaField
              label="Duration"
              placeholder="Duration of concert..."
              value={formData.duration}
              onChange={(v) => updateField('duration', v)}
            />
            <TextAreaField
              label="Backline"
              placeholder="Instrument backline info..."
              value={formData.backline}
              onChange={(v) => updateField('backline', v)}
            />
            <TextAreaField
              label="Technique"
              placeholder="Sound / lights..."
              value={formData.technique}
              onChange={(v) => updateField('technique', v)}
            />
            <TextAreaField
              label="Other Info"
              placeholder="Any other notes..."
              value={formData.other_info}
              onChange={(v) => updateField('other_info', v)}
            />
          </div>

          {/* Column 3: Progress */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-dark-text-primary border-b border-dark-border pb-1.5">
              Todo
              <SectionCheck complete={isChecklistComplete(formData.checklist)} />
            </h3>

            <TextAreaField
              label="Admin"
              placeholder="Person or organism in charge of admin..."
              value={formData.admin_contact}
              onChange={(v) => updateField('admin_contact', v)}
            />

            {/* Checklist */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-dark-text-secondary">Checklist</label>
              <div className="space-y-1.5">
                {formData.checklist.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-dark-text-primary">
                    <input
                      type="checkbox"
                      checked={item.done}
                      onChange={() => {
                        const updated = [...formData.checklist];
                        updated[idx] = { ...updated[idx], done: !updated[idx].done };
                        setFormData({ ...formData, checklist: updated });
                      }}
                      className="accent-primary-500 w-3.5 h-3.5 cursor-pointer"
                    />
                    <span className={`flex-1 ${item.done ? 'line-through text-dark-text-muted' : ''}`}>{item.label}</span>
                    <button
                      type="button"
                      onClick={() => setChecklistDeleteIdx(idx)}
                      className="text-dark-text-muted hover:text-red-400 transition-colors p-0.5"
                      title="Remove item"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="text"
                  className="input flex-1 text-sm"
                  placeholder="Add checklist item..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const value = (e.target as HTMLInputElement).value.trim();
                      if (value) {
                        setFormData({ ...formData, checklist: [...formData.checklist, { label: value, done: false }] });
                        (e.target as HTMLInputElement).value = '';
                      }
                    }
                  }}
                />
                <Button
                  variant="ghost"
                  onClick={(e) => {
                    const input = (e.currentTarget as HTMLElement).previousElementSibling as HTMLInputElement;
                    const value = input.value.trim();
                    if (value) {
                      setFormData({ ...formData, checklist: [...formData.checklist, { label: value, done: false }] });
                      input.value = '';
                      input.focus();
                    }
                  }}
                >
                  Add
                </Button>
              </div>
              <ConfirmDialog
                isOpen={checklistDeleteIdx !== null}
                onClose={() => setChecklistDeleteIdx(null)}
                onConfirm={() => {
                  if (checklistDeleteIdx !== null) {
                    const updated = formData.checklist.filter((_, i) => i !== checklistDeleteIdx);
                    setFormData({ ...formData, checklist: updated });
                    setChecklistDeleteIdx(null);
                  }
                }}
                title="Remove checklist item"
                message={checklistDeleteIdx !== null ? `Remove "${formData.checklist[checklistDeleteIdx]?.label}"?` : ''}
                confirmLabel="Remove"
                danger
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
